import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Trophy, RefreshCw, FileDown, CheckCircle2, XCircle, Clock, AlertTriangle, Ship, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${window.location.origin}/api`;

const STATUS_CONFIG = {
    atingiu: { label: 'Atingiu Meta', color: 'text-emerald-300 bg-emerald-900/40 border-emerald-700', icon: CheckCircle2 },
    nao_atingiu: { label: 'Nao Atingiu', color: 'text-red-300 bg-red-900/40 border-red-700', icon: XCircle },
    em_avaliacao: { label: 'Em Avaliacao', color: 'text-amber-300 bg-amber-900/40 border-amber-700', icon: Clock },
};

const semestresDisponiveis = () => {
    const now = new Date();
    const ano = now.getFullYear();
    const opcs = [];
    for (let a = ano; a >= ano - 2; a--) {
        opcs.push(`${a}-S2`, `${a}-S1`);
    }
    return opcs;
};

export default function DesempenhoPLRPage() {
    const { hasRole } = useAuth();
    const canRecalc = hasRole(['admin', 'gestor']);
    const [periodo, setPeriodo] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-S${now.getMonth() < 6 ? 1 : 2}`;
    });
    const [data, setData] = useState({ resumo: { total: 0, atingiu: 0, nao_atingiu: 0, em_avaliacao: 0 }, embarcacoes: [] });
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [recalculando, setRecalculando] = useState(false);
    const [filtroStatus, setFiltroStatus] = useState('todos');
    const [filtroTipo, setFiltroTipo] = useState('todos');
    const [gerandoPdf, setGerandoPdf] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/operacao/desempenho-plr`, { params: { periodo } });
            setData(r.data);
        } catch {
            toast.error('Erro ao carregar desempenho PLR');
        } finally {
            setLoading(false);
        }
    }, [periodo]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const abrirDetalhe = async (emb) => {
        try {
            const r = await axios.get(`${API}/operacao/desempenho-plr/${emb.embarcacao_id}`, { params: { periodo } });
            setDetail(r.data);
            setDetailOpen(true);
        } catch {
            toast.error('Erro ao carregar detalhe');
        }
    };

    const recalcular = async (emb) => {
        if (!canRecalc) return;
        setRecalculando(true);
        try {
            const r = await axios.post(`${API}/operacao/desempenho-plr/${emb.embarcacao_id}/recalcular`, null, { params: { periodo } });
            setDetail(r.data);
            toast.success('Recalculado com sucesso');
            fetchData();
        } catch {
            toast.error('Erro ao recalcular');
        } finally {
            setRecalculando(false);
        }
    };

    const gerarPdf = async () => {
        setGerandoPdf(true);
        try {
            const r = await axios.get(`${API}/operacao/desempenho-plr/relatorio/pdf`, { params: { periodo }, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `desempenho_plr_${periodo}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Relatorio gerado!');
        } catch {
            toast.error('Erro ao gerar relatorio');
        } finally {
            setGerandoPdf(false);
        }
    };

    const filtradas = (data.embarcacoes || []).filter(e => {
        if (filtroStatus !== 'todos' && e.status !== filtroStatus) return false;
        if (filtroTipo !== 'todos' && e.tipo !== filtroTipo) return false;
        return true;
    });

    const pct = data.resumo.total > 0 ? ((data.resumo.atingiu / data.resumo.total) * 100).toFixed(1) : 0;

    return (
        <div className="p-6 space-y-6" data-testid="desempenho-plr-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-heading text-white flex items-center gap-2">
                        <Trophy className="w-7 h-7 text-amber-400" />
                        Desempenho PLR
                    </h1>
                    <p className="text-sm text-slate-400 font-mono mt-1">
                        Participacao nos Lucros e Resultados - Avaliacao semestral
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Select value={periodo} onValueChange={setPeriodo}>
                        <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-white" data-testid="select-periodo-plr">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                            {semestresDisponiveis().map(p => (
                                <SelectItem key={p} value={p}>{p.replace('-S1', ' / 1o Sem').replace('-S2', ' / 2o Sem')}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        onClick={gerarPdf}
                        disabled={gerandoPdf || loading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="btn-pdf-plr"
                    >
                        <FileDown className="w-4 h-4 mr-2" />
                        {gerandoPdf ? 'Gerando...' : 'Relatorio PDF'}
                    </Button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                        <p className="text-xs text-slate-400 font-mono uppercase">Total Embarcacoes</p>
                        <p className="text-3xl text-white font-bold mt-1">{data.resumo.total}</p>
                    </CardContent>
                </Card>
                <Card className="bg-emerald-950/40 border-emerald-800/60">
                    <CardContent className="p-4">
                        <p className="text-xs text-emerald-400 font-mono uppercase">Atingiu Meta</p>
                        <p className="text-3xl text-emerald-300 font-bold mt-1">{data.resumo.atingiu} <span className="text-base text-emerald-500">({pct}%)</span></p>
                    </CardContent>
                </Card>
                <Card className="bg-red-950/40 border-red-800/60">
                    <CardContent className="p-4">
                        <p className="text-xs text-red-400 font-mono uppercase">Nao Atingiu</p>
                        <p className="text-3xl text-red-300 font-bold mt-1">{data.resumo.nao_atingiu}</p>
                    </CardContent>
                </Card>
                <Card className="bg-amber-950/40 border-amber-800/60">
                    <CardContent className="p-4">
                        <p className="text-xs text-amber-400 font-mono uppercase">Em Avaliacao</p>
                        <p className="text-3xl text-amber-300 font-bold mt-1">{data.resumo.em_avaliacao}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Filtros */}
            <div className="flex items-center gap-3 flex-wrap">
                <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                    <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="todos">Todos os Status</SelectItem>
                        <SelectItem value="atingiu">Atingiu</SelectItem>
                        <SelectItem value="nao_atingiu">Nao Atingiu</SelectItem>
                        <SelectItem value="em_avaliacao">Em Avaliacao</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                    <SelectTrigger className="w-44 bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="todos">Todos os Tipos</SelectItem>
                        <SelectItem value="empurrador">Empurrador</SelectItem>
                        <SelectItem value="balsa">Balsa</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Tabela */}
            <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                    <CardTitle className="text-white font-heading text-base">
                        Embarcacoes - {periodo.replace('-S1', ' / 1o Semestre').replace('-S2', ' / 2o Semestre')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-slate-400 text-center py-8">Carregando...</p>
                    ) : filtradas.length === 0 ? (
                        <p className="text-slate-500 text-center py-8 italic">Nenhuma embarcacao encontrada.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="border-slate-800 hover:bg-transparent">
                                    <TableHead className="text-slate-400">Embarcacao</TableHead>
                                    <TableHead className="text-slate-400">Tipo</TableHead>
                                    <TableHead colSpan={4} className="text-slate-500 text-center text-[10px] uppercase font-mono border-b border-slate-800 py-1">Operacional / Seguranca</TableHead>
                                    <TableHead colSpan={3} className="text-amber-500 text-center text-[10px] uppercase font-mono border-b border-amber-900/40 py-1 bg-amber-950/20">Petrobras (Contratual)</TableHead>
                                    <TableHead className="text-slate-400">Status</TableHead>
                                    <TableHead className="text-slate-400 text-right">Acoes</TableHead>
                                </TableRow>
                                <TableRow className="border-slate-800 hover:bg-transparent">
                                    <TableHead className="text-slate-400"></TableHead>
                                    <TableHead className="text-slate-400"></TableHead>
                                    <TableHead className="text-slate-400 text-center text-[10px]">Acid.</TableHead>
                                    <TableHead className="text-slate-400 text-center text-[10px]">Abalr.</TableHead>
                                    <TableHead className="text-slate-400 text-center text-[10px]">Notif.</TableHead>
                                    <TableHead className="text-slate-400 text-center text-[10px]">Limp.</TableHead>
                                    <TableHead className="text-amber-400 text-center text-[10px] bg-amber-950/20">Ciclo</TableHead>
                                    <TableHead className="text-amber-400 text-center text-[10px] bg-amber-950/20">Bunker</TableHead>
                                    <TableHead className="text-amber-400 text-center text-[10px] bg-amber-950/20">Op.</TableHead>
                                    <TableHead className="text-slate-400"></TableHead>
                                    <TableHead className="text-slate-400"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtradas.map((e) => {
                                    const cfg = STATUS_CONFIG[e.status] || STATUS_CONFIG.em_avaliacao;
                                    const Icon = cfg.icon;
                                    const TipoIcon = e.tipo === 'empurrador' ? Truck : Ship;
                                    const c = Object.fromEntries((e.criterios || []).map(ci => [ci.id, ci]));
                                    const marker = (atingido) => atingido ? (
                                        <CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" />
                                    ) : (
                                        <XCircle className="w-4 h-4 text-red-400 mx-auto" />
                                    );
                                    return (
                                        <TableRow key={e.embarcacao_id} className="border-slate-800 hover:bg-slate-800/40 cursor-pointer" onClick={() => abrirDetalhe(e)} data-testid={`row-plr-${e.embarcacao_id}`}>
                                            <TableCell className="text-white font-medium">{e.embarcacao_nome}</TableCell>
                                            <TableCell className="text-slate-400">
                                                <span className="inline-flex items-center gap-1">
                                                    <TipoIcon className="w-3.5 h-3.5" />
                                                    {e.tipo}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-center">{marker(c.acidentes?.atingido)}</TableCell>
                                            <TableCell className="text-center">{marker(c.abalroamento?.atingido)}</TableCell>
                                            <TableCell className="text-center">{marker(c.petrobras?.atingido)}</TableCell>
                                            <TableCell className="text-center">{marker(c.limpeza?.atingido)}</TableCell>
                                            <TableCell className="text-center bg-amber-950/10">{marker(c.ciclo_abastecimento?.atingido)}</TableCell>
                                            <TableCell className="text-center bg-amber-950/10">{marker(c.avaliacao_bunker?.atingido)}</TableCell>
                                            <TableCell className="text-center bg-amber-950/10">{marker(c.resultado_balsa?.atingido)}</TableCell>
                                            <TableCell>
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase border ${cfg.color}`}>
                                                    <Icon className="w-3 h-3" /> {cfg.label}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(ev) => { ev.stopPropagation(); abrirDetalhe(e); }}>
                                                    Detalhes
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Dialog Detalhe */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-amber-400" />
                            Desempenho PLR - {detail?.embarcacao_nome}
                        </DialogTitle>
                    </DialogHeader>
                    {detail && (
                        <div className="space-y-4 mt-2">
                            <div className="flex items-center gap-3 flex-wrap">
                                {(() => {
                                    const cfg = STATUS_CONFIG[detail.status] || STATUS_CONFIG.em_avaliacao;
                                    const Icon = cfg.icon;
                                    return (
                                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-sm text-sm font-mono uppercase border ${cfg.color}`}>
                                            <Icon className="w-4 h-4" /> {cfg.label}
                                        </span>
                                    );
                                })()}
                                <span className="text-xs text-slate-400 font-mono">
                                    Periodo: {detail.data_inicio} a {detail.data_fim}
                                </span>
                                {canRecalc && (
                                    <Button size="sm" onClick={() => recalcular(detail)} disabled={recalculando} className="ml-auto bg-cyan-600 hover:bg-cyan-700">
                                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${recalculando ? 'animate-spin' : ''}`} />
                                        {recalculando ? 'Recalculando...' : 'Recalcular'}
                                    </Button>
                                )}
                            </div>

                            <div className="space-y-3">
                                {(detail.criterios || []).map(c => (
                                    <Card key={c.id} className={`${c.atingido ? (c.categoria === 'petrobras' ? 'bg-amber-950/20 border-amber-900/60' : 'bg-emerald-950/20 border-emerald-900/60') : 'bg-red-950/20 border-red-900/60'}`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    {c.atingido ? (
                                                        <CheckCircle2 className={`w-5 h-5 ${c.categoria === 'petrobras' ? 'text-amber-400' : 'text-emerald-400'}`} />
                                                    ) : (
                                                        <AlertTriangle className="w-5 h-5 text-red-400" />
                                                    )}
                                                    <h3 className={`font-semibold ${c.atingido ? (c.categoria === 'petrobras' ? 'text-amber-200' : 'text-emerald-300') : 'text-red-300'}`}>
                                                        {c.nome}
                                                    </h3>
                                                    {c.categoria === 'petrobras' && (
                                                        <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-amber-900/40 text-amber-300 border border-amber-800/60">
                                                            Petrobras
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-xs text-slate-400 font-mono">
                                                    {c.violacoes.length} ocorrencia(s)
                                                </span>
                                            </div>
                                            {!c.atingido && c.violacoes.length > 0 && (
                                                <div className="mt-3 space-y-2">
                                                    {c.violacoes.map((v, i) => (
                                                        <div key={i} className="bg-slate-900/60 border-l-2 border-red-500 p-2 rounded-sm" data-testid={`violacao-${c.id}-${i}`}>
                                                            <p className="text-xs text-red-400 font-mono uppercase">{v.fonte} - {v.referencia}</p>
                                                            <p className="text-sm text-slate-200 mt-1">{v.titulo || v.detalhe}</p>
                                                            <p className="text-xs text-slate-500 mt-1">Data: {v.data}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>

                            <p className="text-[10px] text-slate-600 font-mono text-right">
                                Calculado em: {detail.calculado_em?.slice(0, 19).replace('T', ' ')} UTC
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
